import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Settings, X, Clock, Check, Trash2, Calendar, MapPin, ChevronRight, MessageCircle, Car, DollarSign, LogOut, AlertTriangle, Navigation, Users } from 'lucide-react';
import { toast } from 'sonner';
import notificationService from '@/services/NotificationService';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationCenter({ userId, userRole, onOpenChat, token }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications'); // 'notifications' or 'rides'
  const [notifications, setNotifications] = useState([]);
  const [bookedRides, setBookedRides] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedRide, setSelectedRide] = useState(null); // For ride details modal
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leavingRide, setLeavingRide] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: true,
    browserPush: true,
    inApp: true,
    reminderTimes: [30, 15],
    sound: true
  });
  const [permissionGranted, setPermissionGranted] = useState(false);
  const dropdownRef = useRef(null);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleigBisSB0rthJzpr6dJaJRQ7pHpREA==');
      audio.volume = 0.5;
      audio.play().catch(() => { /* Ignore audio play errors */ });
    } catch (e) {
      // Ignore errors when playing notification sound
    }
  }, []);

  // Fetch booked/upcoming rides for the user
  const fetchBookedRides = useCallback(async () => {
    if (!token) return;
    
    try {
      // Fetch user's active/upcoming rides
      const response = await axios.get(`${API}/rides/my-rides`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter to only show upcoming/active rides (not completed or cancelled)
      const activeRides = (response.data || []).filter(ride => 
        !['completed', 'cancelled'].includes(ride.status)
      );
      
      setBookedRides(activeRides);
    } catch (error) {
      console.log('Could not fetch booked rides:', error.message);
    }
  }, [token]);

  // Trigger ride reminder notification
  const triggerRideReminder = useCallback(async (ride, minutesUntil) => {
    if (notificationSettings.inApp) {
      const newNotification = {
        id: `ride_reminder_${ride.id}_${minutesUntil}`,
        type: 'ride_reminder',
        title: `Ride in ${minutesUntil} minutes`,
        message: `Pickup: ${ride.pickup_address}`,
        rideId: ride.id,
        read: false,
        createdAt: new Date().toISOString()
      };
      
      setNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      if (notificationSettings.sound) {
        playNotificationSound();
      }
    }

    if (notificationSettings.browserPush && permissionGranted) {
      await notificationService.showRideReminder(ride, minutesUntil);
    }
  }, [notificationSettings, permissionGranted, playNotificationSound]);

  // Check for upcoming scheduled rides
  const checkUpcomingRides = useCallback(async () => {
    if (!notificationSettings.enabled) return;

    try {
      const endpoint = userRole === 'driver' ? `${API}/rides/scheduled` : `${API}/rides/my-rides`;
      const response = await axios.get(endpoint, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const rides = response.data || [];
      const now = Date.now();

      rides.forEach(ride => {
        if (!ride.scheduled_time) return;

        const rideTime = new Date(ride.scheduled_time).getTime();
        const minutesUntil = Math.round((rideTime - now) / (1000 * 60));

        notificationSettings.reminderTimes.forEach(reminderMinutes => {
          if (minutesUntil <= reminderMinutes && minutesUntil > reminderMinutes - 1) {
            const notificationKey = `ride_${ride.id}_${reminderMinutes}`;
            const alreadyNotified = localStorage.getItem(notificationKey);
            
            if (!alreadyNotified) {
              triggerRideReminder(ride, minutesUntil);
              localStorage.setItem(notificationKey, 'true');
              setTimeout(() => localStorage.removeItem(notificationKey), reminderMinutes * 60 * 1000 + 60000);
            }
          }
        });
      });
    } catch (error) {
      console.error('Failed to check upcoming rides:', error);
    }
  }, [notificationSettings, userRole, triggerRideReminder, token]);

  // Initialize
  useEffect(() => {
    let isMounted = true;
    
    const initNotifications = async () => {
      await notificationService.init();
      if (isMounted) {
        setPermissionGranted(notificationService.isEnabled());
      }
      
      const savedSettings = localStorage.getItem(`notification_settings_${userId}`);
      if (savedSettings && isMounted) {
        setNotificationSettings(JSON.parse(savedSettings));
      }
    };
    
    const loadInitialData = async () => {
      await initNotifications();
      // Initial fetch of booked rides
      if (token && isMounted) {
        try {
          const response = await axios.get(`${API}/rides/my-rides`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (isMounted) {
            const activeRides = (response.data || []).filter(ride => 
              !['completed', 'cancelled'].includes(ride.status)
            );
            setBookedRides(activeRides);
          }
        } catch (error) {
          console.log('Could not fetch initial rides:', error.message);
        }
      }
    };
    
    loadInitialData();

    const checkInterval = setInterval(() => {
      checkUpcomingRides();
      fetchBookedRides();
    }, 60000);
    
    return () => {
      isMounted = false;
      clearInterval(checkInterval);
    };
  }, [userId, checkUpcomingRides, fetchBookedRides, token]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Request notification permission
  const handleRequestPermission = async () => {
    const permission = await notificationService.requestPermission();
    setPermissionGranted(permission === 'granted');
    if (permission === 'granted') {
      toast.success('Notifications enabled!');
    } else if (permission === 'denied') {
      toast.error('Notifications blocked. Please enable in browser settings.');
    }
  };

  // Save notification settings
  const saveSettings = (newSettings) => {
    setNotificationSettings(newSettings);
    localStorage.setItem(`notification_settings_${userId}`, JSON.stringify(newSettings));
    toast.success('Notification settings saved');
  };

  // Toggle reminder time
  const toggleReminderTime = (minutes) => {
    const newTimes = notificationSettings.reminderTimes.includes(minutes)
      ? notificationSettings.reminderTimes.filter(t => t !== minutes)
      : [...notificationSettings.reminderTimes, minutes].sort((a, b) => b - a);
    
    saveSettings({ ...notificationSettings, reminderTimes: newTimes });
  };

  // Mark notification as read
  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Mark all as read
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  // Clear all notifications
  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Format status
  const formatStatus = (status) => {
    if (status === 'pending_driver_acceptance') return 'Awaiting Driver';
    return status?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Unknown';
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      requested: 'text-yellow-400 bg-yellow-500/20',
      pending_driver_acceptance: 'text-orange-400 bg-orange-500/20',
      accepted: 'text-blue-400 bg-blue-500/20',
      partially_filled: 'text-blue-400 bg-blue-500/20',
      en_route: 'text-purple-400 bg-purple-500/20',
      arrived: 'text-green-400 bg-green-500/20',
      in_progress: 'text-neon-cyan bg-cyan-500/20',
      completed: 'text-green-500 bg-green-600/20',
      cancelled: 'text-red-400 bg-red-500/20',
    };
    return colors[status] || 'text-white bg-white/10';
  };

  // Handle chat with driver
  const handleChatWithDriver = (ride) => {
    if (onOpenChat && ride.driver_id) {
      onOpenChat(ride);
      setIsOpen(false);
      setSelectedRide(null);
    } else if (!ride.driver_id) {
      toast.info('No driver assigned yet');
    }
  };

  // Handle ride selection to show details
  const handleRideClick = async (ride) => {
    try {
      // Fetch full ride details
      const response = await axios.get(`${API}/rides/${ride.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedRide(response.data);
    } catch (error) {
      // Fall back to the ride data we have
      setSelectedRide({
        ...ride,
        user_relationship: {
          is_main_rider: ride.rider_id === userId,
          is_joined_rider: ride.riders?.some(r => r.rider_id === userId),
          can_leave: !['completed', 'cancelled', 'arrived', 'in_progress'].includes(ride.status)
        }
      });
    }
  };

  // Handle leaving/opting out of a ride
  const handleLeaveRide = async () => {
    if (!selectedRide) return;
    
    setLeavingRide(true);
    try {
      const response = await axios.post(
        `${API}/rides/${selectedRide.id}/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'You have left the ride');
      setSelectedRide(null);
      setShowLeaveConfirm(false);
      fetchBookedRides(); // Refresh the rides list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave ride');
    } finally {
      setLeavingRide(false);
    }
  };

  const totalCount = unreadCount + bookedRides.length;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) fetchBookedRides();
        }}
        className="relative p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors"
        data-testid="notification-bell-btn"
        title="Notifications & Upcoming Rides"
      >
        <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
        {totalCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {totalCount > 9 ? '9+' : totalCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 glass-panel rounded-2xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-outfit font-bold text-lg">Notifications</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Settings"
                >
                  <Settings className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Tabs */}
            {!showSettings && (
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('notifications')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    activeTab === 'notifications' 
                      ? 'bg-neon-cyan/20 text-neon-cyan' 
                      : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
                >
                  Alerts {unreadCount > 0 && `(${unreadCount})`}
                </button>
                <button
                  onClick={() => setActiveTab('rides')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    activeTab === 'rides' 
                      ? 'bg-neon-purple/20 text-neon-purple' 
                      : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
                  data-testid="my-rides-tab"
                >
                  My Rides {bookedRides.length > 0 && `(${bookedRides.length})`}
                </button>
              </div>
            )}
          </div>

          {/* Settings Panel */}
          {showSettings ? (
            <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
              <h4 className="font-medium text-white/80">Notification Settings</h4>
              
              {/* Enable/Disable */}
              <div className="flex items-center justify-between">
                <span className="text-sm">Enable Notifications</span>
                <button
                  onClick={() => saveSettings({ ...notificationSettings, enabled: !notificationSettings.enabled })}
                  className={`w-12 h-6 rounded-full transition-colors ${notificationSettings.enabled ? 'bg-green-500' : 'bg-white/20'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${notificationSettings.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {/* Browser Push */}
              <div className="flex items-center justify-between">
                <span className="text-sm">Browser Push Notifications</span>
                <div className="flex items-center gap-2">
                  {!permissionGranted && (
                    <button
                      onClick={handleRequestPermission}
                      className="text-xs bg-neon-cyan/20 text-neon-cyan px-2 py-1 rounded"
                    >
                      Enable
                    </button>
                  )}
                  <button
                    onClick={() => saveSettings({ ...notificationSettings, browserPush: !notificationSettings.browserPush })}
                    className={`w-12 h-6 rounded-full transition-colors ${notificationSettings.browserPush ? 'bg-green-500' : 'bg-white/20'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${notificationSettings.browserPush ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>

              {/* Sound */}
              <div className="flex items-center justify-between">
                <span className="text-sm">Notification Sound</span>
                <button
                  onClick={() => saveSettings({ ...notificationSettings, sound: !notificationSettings.sound })}
                  className={`w-12 h-6 rounded-full transition-colors ${notificationSettings.sound ? 'bg-green-500' : 'bg-white/20'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${notificationSettings.sound ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {/* Reminder Times */}
              <div>
                <span className="text-sm text-white/80 block mb-2">Remind me before pickup</span>
                <div className="flex flex-wrap gap-2">
                  {[60, 30, 15, 10, 5].map(mins => (
                    <button
                      key={mins}
                      onClick={() => toggleReminderTime(mins)}
                      className={`px-3 py-1 rounded-full text-xs transition-all ${
                        notificationSettings.reminderTimes.includes(mins)
                          ? 'bg-neon-cyan text-black'
                          : 'bg-white/10 text-white/60 hover:bg-white/20'
                      }`}
                    >
                      {mins} min
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setShowSettings(false)}
                className="w-full py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors"
              >
                Done
              </button>
            </div>
          ) : activeTab === 'notifications' ? (
            /* Notifications List */
            <div className="max-h-96 overflow-y-auto">
              {notifications.length > 0 ? (
                <>
                  <div className="p-2 border-b border-white/10 flex items-center justify-between">
                    <button
                      onClick={markAllAsRead}
                      className="text-xs text-neon-cyan hover:underline"
                    >
                      Mark all as read
                    </button>
                    <button
                      onClick={clearAll}
                      className="text-xs text-red-400 hover:underline flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Clear all
                    </button>
                  </div>
                  {notifications.map(notification => (
                    <div
                      key={notification.id}
                      onClick={() => markAsRead(notification.id)}
                      className={`p-4 border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${
                        !notification.read ? 'bg-white/5' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          notification.type === 'ride_reminder' ? 'bg-neon-cyan/20 text-neon-cyan' : 'bg-white/10'
                        }`}>
                          {notification.type === 'ride_reminder' ? (
                            <Clock className="w-4 h-4" />
                          ) : (
                            <Bell className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`font-medium text-sm ${!notification.read ? 'text-white' : 'text-white/70'}`}>
                              {notification.title}
                            </p>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-neon-cyan rounded-full flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-white/50 truncate">{notification.message}</p>
                          <p className="text-xs text-white/30 mt-1">{formatTimeAgo(notification.createdAt)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">No notifications yet</p>
                </div>
              )}
            </div>
          ) : (
            /* Booked Rides List - Organized by timing */
            <div className="max-h-96 overflow-y-auto">
              {bookedRides.length > 0 ? (
                <>
                  {/* Active/Confirmed Rides Section - rides that are happening now or confirmed */}
                  {(() => {
                    const activeRides = bookedRides.filter(r => 
                      ['accepted', 'en_route', 'arrived', 'in_progress', 'partially_filled'].includes(r.status) ||
                      r.ride_type === 'now'
                    );
                    const scheduledRides = bookedRides.filter(r => 
                      r.ride_type === 'scheduled' && 
                      !['accepted', 'en_route', 'arrived', 'in_progress'].includes(r.status)
                    );
                    const pendingRides = bookedRides.filter(r => 
                      r.status === 'requested' && r.ride_type !== 'now'
                    );
                    
                    return (
                      <>
                        {/* Active Rides */}
                        {activeRides.length > 0 && (
                          <div>
                            <div className="px-4 py-2 bg-neon-cyan/10 border-b border-neon-cyan/20">
                              <h4 className="text-xs font-semibold text-neon-cyan uppercase tracking-wider flex items-center gap-2">
                                <div className="w-2 h-2 bg-neon-cyan rounded-full animate-pulse" />
                                Active Rides ({activeRides.length})
                              </h4>
                            </div>
                            {activeRides.map(ride => (
                              <RideCard key={ride.id} ride={ride} onChat={handleChatWithDriver} onClick={() => handleRideClick(ride)} getStatusColor={getStatusColor} formatStatus={formatStatus} />
                            ))}
                          </div>
                        )}
                        
                        {/* Pending/Requested Rides */}
                        {pendingRides.length > 0 && (
                          <div>
                            <div className="px-4 py-2 bg-yellow-500/10 border-b border-yellow-500/20">
                              <h4 className="text-xs font-semibold text-yellow-400 uppercase tracking-wider flex items-center gap-2">
                                <Clock className="w-3 h-3" />
                                Awaiting Confirmation ({pendingRides.length})
                              </h4>
                            </div>
                            {pendingRides.map(ride => (
                              <RideCard key={ride.id} ride={ride} onChat={handleChatWithDriver} onClick={() => handleRideClick(ride)} getStatusColor={getStatusColor} formatStatus={formatStatus} />
                            ))}
                          </div>
                        )}
                        
                        {/* Scheduled Rides - sorted by time */}
                        {scheduledRides.length > 0 && (
                          <div>
                            <div className="px-4 py-2 bg-neon-purple/10 border-b border-neon-purple/20">
                              <h4 className="text-xs font-semibold text-neon-purple uppercase tracking-wider flex items-center gap-2">
                                <Calendar className="w-3 h-3" />
                                Scheduled Rides ({scheduledRides.length})
                              </h4>
                            </div>
                            {scheduledRides
                              .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))
                              .map(ride => (
                                <RideCard key={ride.id} ride={ride} onChat={handleChatWithDriver} onClick={() => handleRideClick(ride)} getStatusColor={getStatusColor} formatStatus={formatStatus} />
                              ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                <div className="p-8 text-center">
                  <Car className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">No upcoming rides</p>
                  <p className="text-white/30 text-xs mt-1">Book a ride to see it here</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Ride Details Modal */}
      {selectedRide && (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4" onClick={() => setSelectedRide(null)}>
          <div 
            className="bg-dark-surface rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-dark-surface z-10">
              <div className="flex items-center gap-3">
                <Car className="w-6 h-6 text-neon-purple" />
                <div>
                  <h3 className="font-outfit font-bold text-lg">Ride Details</h3>
                  {selectedRide.serial_number && (
                    <p className="text-xs text-white/50 font-mono">{selectedRide.serial_number}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedRide(null)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="close-ride-details"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getStatusColor(selectedRide.status)}`}>
                  {formatStatus(selectedRide.status)}
                </span>
                <span className="text-2xl font-bold text-green-400">${selectedRide.fare}</span>
              </div>

              {/* Route Information */}
              <div className="bg-dark-bg rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-white/50 mb-1">Pickup</p>
                    <p className="text-sm text-white">{selectedRide.pickup_address}</p>
                  </div>
                </div>

                {/* Intermediate Stops */}
                {selectedRide.stops && selectedRide.stops.length > 0 && (
                  <div className="pl-1.5 border-l-2 border-dashed border-white/20 ml-1 space-y-2">
                    {selectedRide.stops.map((stop, idx) => (
                      <div key={idx} className="flex items-start gap-3 pl-3">
                        <div className="w-2.5 h-2.5 rounded-full bg-neon-purple/50 mt-1.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-white/50">Stop {idx + 1}</p>
                          <p className="text-sm text-white/80">{stop.address}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-white/50 mb-1">Dropoff</p>
                    <p className="text-sm text-white">{selectedRide.dropoff_address}</p>
                  </div>
                </div>
              </div>

              {/* Ride Info Grid */}
              <div className="grid grid-cols-2 gap-3">
                {selectedRide.distance && (
                  <div className="bg-dark-bg rounded-xl p-3 text-center">
                    <p className="text-xs text-white/50 mb-1">Distance</p>
                    <p className="text-lg font-bold text-white">{selectedRide.distance} mi</p>
                  </div>
                )}
                {selectedRide.seats_requested && (
                  <div className="bg-dark-bg rounded-xl p-3 text-center">
                    <p className="text-xs text-white/50 mb-1">Seats</p>
                    <p className="text-lg font-bold text-white">{selectedRide.seats_requested}</p>
                  </div>
                )}
                {selectedRide.scheduled_time && (
                  <div className="bg-dark-bg rounded-xl p-3 col-span-2">
                    <p className="text-xs text-white/50 mb-1">Scheduled Time</p>
                    <p className="text-sm font-medium text-neon-purple">
                      {new Date(selectedRide.scheduled_time).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              {/* Driver Info */}
              {selectedRide.driver_name && (
                <div className="bg-dark-bg rounded-xl p-4">
                  <p className="text-xs text-white/50 mb-2">Driver</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-neon-purple/20 flex items-center justify-center text-neon-purple font-bold">
                        {selectedRide.driver_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{selectedRide.driver_name}</p>
                        {selectedRide.driver_username && (
                          <p className="text-xs text-white/50">@{selectedRide.driver_username}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleChatWithDriver(selectedRide)}
                      className="flex items-center gap-2 px-4 py-2 bg-neon-cyan/20 text-neon-cyan hover:bg-neon-cyan hover:text-black rounded-full text-sm font-medium transition-all"
                      data-testid="chat-driver-modal-btn"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Chat
                    </button>
                  </div>
                </div>
              )}

              {/* Other Riders (for shared rides) */}
              {selectedRide.riders && selectedRide.riders.length > 0 && (
                <div className="bg-dark-bg rounded-xl p-4">
                  <p className="text-xs text-white/50 mb-2 flex items-center gap-2">
                    <Users className="w-3 h-3" />
                    Passengers ({selectedRide.riders.length})
                  </p>
                  <div className="space-y-2">
                    {selectedRide.riders.map((rider, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-white/80">{rider.rider_name}</span>
                        <span className="text-white/50">{rider.seats_taken} seat(s)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-2 pt-2">
                {/* Leave/Opt-out Button - Only show if user can leave */}
                {(selectedRide.user_relationship?.can_leave || 
                  (!['completed', 'cancelled', 'arrived', 'in_progress'].includes(selectedRide.status))) && (
                  <button
                    onClick={() => setShowLeaveConfirm(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-xl font-medium transition-all"
                    data-testid="leave-ride-btn"
                  >
                    <LogOut className="w-5 h-5" />
                    Leave This Ride
                  </button>
                )}

                {!selectedRide.driver_name && selectedRide.status === 'requested' && (
                  <div className="flex items-center justify-center gap-2 text-yellow-400/70 text-sm py-2">
                    <div className="w-4 h-4 border-2 border-yellow-400/50 border-t-yellow-400 rounded-full animate-spin" />
                    Waiting for a driver...
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Confirmation Modal */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 bg-black/80 z-[110] flex items-center justify-center p-4" onClick={() => setShowLeaveConfirm(false)}>
          <div 
            className="bg-dark-surface rounded-2xl w-full max-w-sm p-6 shadow-xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="font-outfit font-bold text-lg">Leave Ride?</h3>
                <p className="text-sm text-white/60">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-white/70 text-sm mb-6">
              Are you sure you want to leave this ride? Your seat(s) will become available for other riders to book.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-all"
                disabled={leavingRide}
              >
                Cancel
              </button>
              <button
                onClick={handleLeaveRide}
                disabled={leavingRide}
                className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-leave-btn"
              >
                {leavingRide ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Leaving...
                  </>
                ) : (
                  <>
                    <LogOut className="w-4 h-4" />
                    Leave Ride
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Ride Card Component for displaying individual rides
function RideCard({ ride, onChat, onClick, getStatusColor, formatStatus }) {
  return (
    <div 
      className="p-4 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
      onClick={onClick}
      data-testid={`ride-card-${ride.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Car className="w-5 h-5 text-neon-purple" />
          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(ride.status)}`}>
            {formatStatus(ride.status)}
          </span>
          {ride.ride_type === 'now' && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-neon-cyan/20 text-neon-cyan">
              NOW
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-green-400">${ride.fare}</span>
          <ChevronRight className="w-4 h-4 text-white/40" />
        </div>
      </div>
      
      {/* Serial Number / Tracking ID */}
      {ride.serial_number && (
        <div className="mb-3 text-xs">
          <span className="text-white/40">Tracking: </span>
          <span className="font-mono text-neon-cyan">{ride.serial_number}</span>
        </div>
      )}
      
      <div className="space-y-2 text-sm">
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
          <span className="text-white/80 truncate">{ride.pickup_address}</span>
        </div>
        
        {/* Show intermediate stops if any */}
        {ride.stops && ride.stops.length > 2 && (
          <div className="pl-6 border-l-2 border-dashed border-white/20 ml-1.5 space-y-1">
            {ride.stops.slice(1, -1).map((stop, idx) => (
              <div key={idx} className="flex items-start gap-2 text-xs">
                <div className="w-3 h-3 rounded-full bg-neon-purple/30 flex items-center justify-center text-[8px] text-neon-purple">
                  {idx + 1}
                </div>
                <span className="text-white/60 truncate">{stop.address}</span>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <span className="text-white/80 truncate">{ride.dropoff_address}</span>
        </div>
        
        {ride.scheduled_time && (
          <div className="flex items-center gap-2 text-white/60">
            <Calendar className="w-4 h-4" />
            <span>{new Date(ride.scheduled_time).toLocaleString()}</span>
          </div>
        )}
        
        {/* Show distance if multi-stop */}
        {ride.distance && (
          <div className="flex items-center gap-2 text-white/50 text-xs">
            <span>{ride.distance} mi</span>
            {ride.leg_details && ride.leg_details.length > 1 && (
              <span>• {ride.leg_details.length} stops</span>
            )}
          </div>
        )}
      </div>
      
      {/* Driver Info & Chat */}
      {ride.driver_name && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-neon-purple/20 flex items-center justify-center text-neon-purple font-bold text-sm">
                {ride.driver_name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium">{ride.driver_name}</p>
                <p className="text-xs text-white/50">Driver</p>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onChat(ride); }}
              className="flex items-center gap-1 px-3 py-1.5 bg-neon-cyan/20 text-neon-cyan hover:bg-neon-cyan hover:text-black rounded-full text-xs font-medium transition-all"
              data-testid={`chat-driver-${ride.id}`}
            >
              <MessageCircle className="w-4 h-4" />
              Chat
            </button>
          </div>
        </div>
      )}
      
      {!ride.driver_name && ride.status === 'requested' && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-yellow-400/50 border-t-yellow-400 rounded-full animate-spin" />
            <p className="text-xs text-yellow-400/70">Looking for a driver...</p>
          </div>
        </div>
      )}
      
      {/* Tap to view details hint */}
      <div className="mt-2 text-center">
        <span className="text-xs text-white/30">Tap to view details</span>
      </div>
    </div>
  );
}
